// @ts-nocheck
import * as React from 'react';
import { Field, FieldProps } from 'formik';
import { FormikProps } from 'formik';
import { Mutation, MutationFn } from 'react-apollo';
import { Box, Button, NewFile, StyledFormGroup, Portal } from '.';
import { FaTrash } from 'react-icons/fa';

import CreatePDFLibraryItem from '../graphql/mutations/createPDFLibraryItem';

export type PdfDoc = {
  name: string;
  link: string;
};

type Props = {
  formikProps: FormikProps<any>;
  /**
   * Formik field that stores the PDF list.
   * Expected shape: JSON-stringified array of `{ name, link }`.
   */
  pdfsField: string;
  /**
   * Formik field that stores the current PDF name while uploading.
   * `NewFile` upload only appears once this has a non-empty value.
   */
  pdfNameField: string;
  uploadButtonText?: string;
  currentPdfsLabel?: string;
  pdfNameLabel?: string;
  pdfNamePlaceholder?: string;
  mb?: number;
  mt?: number;
};

/**
 * Parses the Formik `pdfsField` into an array the UI can render.
 * Accepts either:
 * - `[]` already parsed
 * - a JSON string representing `[]`
 */
function parsePdfDocs(value: any): PdfDoc[] {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value as PdfDoc[];
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as PdfDoc[]) : [];
    } catch (e) {
      return [];
    }
  }
  return [];
}

const PdfUploadListField: React.FC<Props> = ({
  formikProps,
  pdfsField,
  pdfNameField,
  uploadButtonText = 'Upload PDF',
  currentPdfsLabel = 'Current PDFs:',
  pdfNameLabel = 'PDF Name',
  pdfNamePlaceholder = 'Enter PDF name',
  mb = 4,
  mt = 4,
}) => {
  /**
   * Uses a local modal toggle.
   * The modal content is mounted via `Portal` so it isn't constrained by parent layout.
   */
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const values: any = (formikProps as any).values || {};
  const currentPdfs = React.useMemo(
    () => parsePdfDocs(values[pdfsField]),
    [values, pdfsField],
  );

  const handleRemovePdf = (index: number) => {
    const updated = currentPdfs.filter((_: any, i: number) => i !== index);
    formikProps.setFieldValue(pdfsField, JSON.stringify(updated));
  };

  const handleAddPdf = async (data: any) => {
    if (!data) {
      return;
    }

    // Requires a name before we render the upload component and append the entry.
    const pdfName = values[pdfNameField];
    if (!pdfName) {
      formikProps.setFieldError(pdfNameField, 'PDF name is required');
      return;
    }

    const updatedPdfs = [
      ...currentPdfs,
      {
        name: pdfName,
        link: data.original,
      },
    ];

    formikProps.setFieldValue(pdfsField, JSON.stringify(updatedPdfs));
    formikProps.setFieldValue(pdfNameField, '');
    setIsModalOpen(false);
  };

  return (
    <>
      <Box mt={mt} mb={mb}>
        <Button
          text={uploadButtonText}
          type="button"
          onClick={() => {
            setIsModalOpen(true);
          }}
        />

        {currentPdfs.length > 0 && (
          <Box mt={3}>
            <Box mb={2} color="gray.600">
              {currentPdfsLabel}
            </Box>

            {currentPdfs.map((pdf: PdfDoc, index: number) => (
              <Box
                key={index}
                p={2}
                mb={1}
                bg="gray.100"
                borderRadius="md"
                display="grid"
                alignItems="center"
                style={{
                  gridTemplateColumns: '1fr auto auto',
                  gap: '20px',
                }}
              >
                <Box textAlign="left">{pdf.name}</Box>

                <Box
                  color="blue.500"
                  cursor="pointer"
                  onClick={() => window.open(pdf.link, '_blank')}
                  style={{ textDecoration: 'underline' }}
                >
                  View PDF
                </Box>

                <Box
                  as="span"
                  color="red.500"
                  cursor="pointer"
                  onClick={() => handleRemovePdf(index)}
                  style={{
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FaTrash />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {isModalOpen && (
        <Portal>
          <Box
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 100000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <Box
              bg="white"
              p={4}
              borderRadius="md"
              width="600px"
              style={{
                maxHeight: '80vh',
                overflowY: 'auto',
              }}
            >
              <Box mb={4}>
                <Field
                  name={pdfNameField}
                  render={({ field }: FieldProps<any>) => (
                    <StyledFormGroup
                      {...field}
                      field={field}
                      form={formikProps}
                      label={pdfNameLabel}
                      placeholder={pdfNamePlaceholder}
                    />
                  )}
                />
              </Box>

              <Mutation mutation={CreatePDFLibraryItem}>
                {(createPDF: MutationFn) => (
                  <Box mb={4}>
                    {values[pdfNameField] && (
                      <NewFile
                        uploadText="ADD PDF"
                        onUploadComplete={handleAddPdf}
                        acceptedFileTypes={['.pdf']}
                      />
                    )}
                  </Box>
                )}
              </Mutation>

              <Box display="flex" justifyContent="flex-end">
                <Button text="Close" type="button" onClick={() => setIsModalOpen(false)} />
              </Box>
            </Box>
          </Box>
        </Portal>
      )}
    </>
  );
};

export default PdfUploadListField;



// WEBPACK FOOTER //
// ./src/components/PdfUploadListField.tsx